import { useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  BookOpen,
  Braces,
  CalendarClock,
  Check,
  CircleOff,
  Clock3,
  FileText,
  ListChecks,
  PencilLine,
  Play,
  Tag,
  Users,
} from 'lucide-react';
import type { RecurringRule, Template } from '../../app/legacy-types';

type TemplateDetailTab = 'content' | 'checklist' | 'schedules';

type TemplateDetailWorkspaceProps = {
  template: Template;
  recurringRules: RecurringRule[];
  canManage: boolean;
  onUse: () => void;
  onEdit: () => void;
};

const TABS: Array<{ id: TemplateDetailTab; label: string; icon: typeof FileText }> = [
  { id: 'content', label: 'Содержание', icon: BookOpen },
  { id: 'checklist', label: 'Чек-лист', icon: ListChecks },
  { id: 'schedules', label: 'Расписания', icon: CalendarClock },
];

const PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Низкий',
  NORMAL: 'Обычный',
  HIGH: 'Высокий',
  CRITICAL: 'Критический',
};

const FREQUENCY_LABELS: Record<string, string> = {
  DAILY: 'Ежедневно',
  WEEKLY: 'Еженедельно',
  MONTHLY: 'Ежемесячно',
};

function formatScheduleDate(value?: string | null) {
  if (!value) return 'Ещё не запускалось';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Дата не определена';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function templateVariables(template: Template) {
  const values = `${template.subject_template}\n${template.body_template}`.match(/{{\s*[^{}]+?\s*}}/g) || [];
  return [...new Set(values.map(value => value.replace(/\s+/g, ' ')))];
}

export function TemplateDetailWorkspace({
  template,
  recurringRules,
  canManage,
  onUse,
  onEdit,
}: TemplateDetailWorkspaceProps) {
  const [tab, setTab] = useState<TemplateDetailTab>('content');
  const tabsId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const checklistCount = template.checklist_template.reduce((total, group) => total + group.items.length, 0);
  const variables = templateVariables(template);

  function selectTab(nextTab: TemplateDetailTab, focus = false) {
    setTab(nextTab);
    if (focus) {
      const nextIndex = TABS.findIndex(item => item.id === nextTab);
      requestAnimationFrame(() => tabRefs.current[nextIndex]?.focus());
    }
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    else return;
    event.preventDefault();
    selectTab(TABS[nextIndex].id, true);
  }

  return (
    <section className="template-detail-workspace" aria-label="Карточка шаблона">
      <header className="template-detail-header">
        <div className="template-detail-heading">
          <span className="template-detail-heading__icon"><FileText aria-hidden="true" /></span>
          <div>
            <div className="template-detail-heading__status-row">
              <span className={`template-state-pill ${template.is_active ? 'is-active' : 'is-inactive'}`}>
                {template.is_active ? <Check aria-hidden="true" /> : <CircleOff aria-hidden="true" />}
                {template.is_active ? 'Активен' : 'Неактивен'}
              </span>
              <span className={`template-priority template-priority--${template.priority.toLowerCase()}`}>
                {PRIORITY_LABELS[template.priority] || template.priority}
              </span>
            </div>
            <h2>{template.name}</h2>
            <p>{template.description || 'Описание шаблона не заполнено'}</p>
          </div>
        </div>
        <div className="template-detail-actions">
          {canManage && (
            <button type="button" className="button ghost" onClick={onEdit}>
              <PencilLine aria-hidden="true" /> Редактировать
            </button>
          )}
          <button type="button" className="button primary" onClick={onUse} disabled={!template.is_active}>
            <Play aria-hidden="true" /> Использовать шаблон
          </button>
        </div>
        {!template.is_active && (
          <p className="template-use-hint">Включите шаблон, чтобы использовать его в заявке</p>
        )}
      </header>

      <div className="template-detail-facts" aria-label="Параметры шаблона">
        <span><Users aria-hidden="true" /><small>Очередь</small><b>{template.queue_name || 'Не назначена'}</b></span>
        <span><Tag aria-hidden="true" /><small>Категория</small><b>{template.category || 'Не указана'}</b></span>
        <span><ListChecks aria-hidden="true" /><small>Чек-лист</small><b>{checklistCount} пунктов</b></span>
        <span><CalendarClock aria-hidden="true" /><small>Расписания</small><b>{recurringRules.length}</b></span>
      </div>

      <div className="template-detail-tabs" role="tablist" aria-label="Разделы шаблона">
        {TABS.map((item, index) => {
          const Icon = item.icon;
          const selected = tab === item.id;
          const count = item.id === 'checklist' ? checklistCount : item.id === 'schedules' ? recurringRules.length : null;
          return (
            <button
              key={item.id}
              ref={node => { tabRefs.current[index] = node; }}
              id={`${tabsId}-${item.id}-tab`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${tabsId}-${item.id}-panel`}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'active' : undefined}
              onClick={() => selectTab(item.id)}
              onKeyDown={event => handleTabKeyDown(event, index)}
            >
              <Icon aria-hidden="true" />
              <span>{item.label}</span>
              {count !== null && <em>{count}</em>}
            </button>
          );
        })}
      </div>

      <div
        className="template-detail-panel"
        id={`${tabsId}-${tab}-panel`}
        role="tabpanel"
        aria-labelledby={`${tabsId}-${tab}-tab`}
        tabIndex={0}
      >
        {tab === 'content' && (
          <div className="template-content-panel">
            <section className="template-ticket-copy">
              <div className="template-section-title">
                <span><FileText aria-hidden="true" /></span>
                <div><h3>Будущая заявка</h3><p>Так сотрудник увидит заполненный шаблон</p></div>
              </div>
              <article className="template-ticket-preview">
                <small>Тема заявки</small>
                <h4>{template.subject_template || 'Тема не заполнена'}</h4>
                <span />
                <small>Описание</small>
                <p>{template.body_template || 'Описание не заполнено'}</p>
              </article>
            </section>
            <aside className="template-variables-panel">
              <div className="template-section-title">
                <span><Braces aria-hidden="true" /></span>
                <div><h3>Переменные</h3><p>Замените их при создании заявки</p></div>
              </div>
              {variables.length ? (
                <div className="template-variable-list">
                  {variables.map(variable => <code key={variable}>{variable}</code>)}
                </div>
              ) : (
                <div className="template-inline-empty"><Braces aria-hidden="true" /><b>Переменные не используются</b><p>Текст будет перенесён в заявку без подстановок.</p></div>
              )}
            </aside>
          </div>
        )}

        {tab === 'checklist' && (
          template.checklist_template.length ? (
            <div className="template-checklist-panel">
              {template.checklist_template.map((group, groupIndex) => (
                <section key={`${group.title}-${groupIndex}`}>
                  <header><span>{String(groupIndex + 1).padStart(2, '0')}</span><div><h3>{group.title || 'Группа без названия'}</h3><p>{group.items.length} пунктов</p></div></header>
                  <ol>
                    {group.items.map((item, itemIndex) => (
                      <li key={`${item}-${itemIndex}`}><span><Check aria-hidden="true" /></span><b>{item}</b></li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          ) : (
            <div className="template-detail-empty"><ListChecks aria-hidden="true" /><h3>Чек-лист не добавлен</h3><p>Шаблон создаёт заявку без обязательных этапов выполнения.</p></div>
          )
        )}

        {tab === 'schedules' && (
          recurringRules.length ? (
            <div className="template-schedules-panel">
              {recurringRules.map(rule => (
                <article key={rule.id}>
                  <span className="template-schedule-icon"><CalendarClock aria-hidden="true" /></span>
                  <div className="template-schedule-name"><b>{rule.name}</b><small>{rule.company_name}</small></div>
                  <span><small>Периодичность</small><b>{FREQUENCY_LABELS[rule.frequency] || rule.frequency}{rule.interval_count > 1 ? ` · каждые ${rule.interval_count}` : ''}</b></span>
                  <span><small>Следующий запуск</small><b>{formatScheduleDate(rule.next_run_at)}</b></span>
                  <span><small>Последний запуск</small><b>{formatScheduleDate(rule.last_run_at)}</b></span>
                  <span className={`template-state-pill ${rule.is_active ? 'is-active' : 'is-inactive'}`}>
                    {rule.is_active ? 'Активно' : 'Остановлено'}
                  </span>
                </article>
              ))}
            </div>
          ) : (
            <div className="template-detail-empty"><Clock3 aria-hidden="true" /><h3>Расписаний пока нет</h3><p>Автоматическое создание заявок для этого шаблона не настроено.</p></div>
          )
        )}
      </div>
    </section>
  );
}
